import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Send } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { Alert, ICON } from "../components/ui";
import { Loadable, PageHead, post, type Row, Table, text, useAdmin, when } from "./ui";

// Chat clienți (owner 2026-11-03): the restaurants' support conversations. The inbox shows what the team has not
// read; a conversation refreshes every 10 s while open, and reading it marks the restaurant's messages as read.

export function ChatInbox() {
  const q = useAdmin<{ chats: Row[]; unread: number }>(["chats"], "/chats");
  return (
    <>
      <PageHead
        title="Chat clienți"
        sub="Conversațiile restaurantelor cu echipa Meniova. Clientul primește răspunsul în aplicație și pe e-mail."
      />
      <Loadable q={q}>
        {(d) => (
          <Table
            head={["Restaurant", "Ultimul mesaj", "De la", "Când", "Necitite"]}
            empty="Niciun restaurant nu a scris încă."
            rows={d.chats.map((c) => [
              <Link key="r" to={`/admin/chat/${String(c.restaurant_id)}`} style={{ fontWeight: 600 }}>
                {text(c.name)}
              </Link>,
              <span key="b" style={{ display: "block", maxWidth: 420, overflowWrap: "anywhere" }}>
                {String(c.last_body ?? "").slice(0, 140)}
              </span>,
              c.last_from === "support" ? "echipa" : "restaurant",
              when(c.last_message_at),
              Number(c.unread) ? (
                <span key="u" className="admin-badge">
                  {text(c.unread)}
                </span>
              ) : c.status === "closed" ? (
                "rezolvat"
              ) : (
                "—"
              ),
            ])}
          />
        )}
      </Loadable>
    </>
  );
}

interface Thread {
  restaurant: string;
  messages: { id: string; from: "restaurant" | "support"; author: string; body: string; createdAt: number }[];
  status: "open" | "closed";
}

export function ChatThread() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const q = useAdmin<Thread>(["chat", id], `/chats/${id}`);
  const [textValue, setText] = useState("");
  const end = useRef<HTMLDivElement>(null);
  const count = q.data?.messages.length ?? 0;
  useEffect(() => {
    if (count) end.current?.scrollIntoView({ block: "end" });
    void qc.invalidateQueries({ queryKey: ["admin", "chats"] });
    void qc.invalidateQueries({ queryKey: ["admin", "business"] });
  }, [count, qc]);
  useEffect(() => {
    const t = setInterval(() => void q.refetch(), 10_000);
    return () => clearInterval(t);
  }, [q]);
  const send = useMutation({
    mutationFn: (body: string) => post<Thread>(`/chats/${id}`, { body }),
    onSuccess: (t) => {
      qc.setQueryData(["admin", "chat", id], t);
      setText("");
    },
  });
  const close = useMutation({
    mutationFn: () => post(`/chats/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (textValue.trim() && !send.isPending) send.mutate(textValue.trim());
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };
  return (
    <Loadable q={q}>
      {(d) => (
        <>
          <Link to="/admin/chat">← Chat clienți</Link>
          <PageHead
            title={d.restaurant}
            sub={<Link to={`/admin/restaurante/${id}`}>Fișa restaurantului</Link>}
            actions={
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={close.isPending || d.status === "closed"}
                onClick={() => close.mutate()}
              >
                <Check {...ICON} aria-hidden="true" />
                {d.status === "closed" ? "Rezolvat" : "Marchează rezolvat"}
              </button>
            }
          />
          <section className="card chat" aria-label={`Conversația cu ${d.restaurant}`}>
            <div className="chat-log" aria-live="polite">
              {!d.messages.length && <p className="muted">Nicio conversație încă. Poți scrie tu primul.</p>}
              {d.messages.map((m) => (
                <div key={m.id} className={`chat-msg ${m.from === "support" ? "mine" : "theirs"}`}>
                  <span className="chat-meta">
                    {m.from === "support" ? "Echipa Meniova" : m.author} · {when(m.createdAt)}
                  </span>
                  <span className="chat-body">{m.body}</span>
                </div>
              ))}
              <div ref={end} />
            </div>
            <form className="chat-form" onSubmit={submit}>
              <label className="sr-only" htmlFor="admin-chat-text">
                Răspunsul echipei
              </label>
              <textarea
                id="admin-chat-text"
                className="field-input"
                rows={2}
                maxLength={4000}
                placeholder="Răspunde… (Enter trimite, Shift+Enter rând nou)"
                value={textValue}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={send.isPending || !textValue.trim()}
              >
                <Send {...ICON} aria-hidden="true" />
                Trimite
              </button>
            </form>
            {send.isError && (
              <Alert tone="error" live>
                {(send.error as Error).message}
              </Alert>
            )}
          </section>
        </>
      )}
    </Loadable>
  );
}
